// The League Table's live adapter (Track 2, T2-1): app-api's `/competitions`, `/seasons`
// and `/league-table` → the same ProtoLeagueTable the snapshot hook returns, so the widget
// doesn't change. The standings rows are already the same shape (both come from the same
// calculator); the adapter only maps leagues + seasons.

import type {
  CompetitionSummary,
  CompetitionsResponse,
  LeagueTableResponse,
  ProtoLeagueTable,
  SeasonsResponse,
} from "@catbento/shared";
import type { CompetitionId, LeagueChoice } from "../data/league.js";
import { LiveApiError, type LiveApi } from "./api.js";

/** A live league: also its gender (`competitionWomens`; null = the DB doesn't say) — a Squad
 *  row dragged out notes it as hers (a club in a women's league is a women's team). */
export interface LiveLeagueChoice extends LeagueChoice {
  womens: boolean | null;
  /** Its country ("Spain"; null = international) and ISO alpha-3 code ("ESP", "ENG") — the
   *  League chip's flag and country groups (Ben, 2026-10-16). */
  countryName: string | null;
  countryCode: string | null;
  /** The DB's youth flag (#329; null = the DB doesn't say): youth leagues go last in their
   *  country's list (modules/CountryFlag.tsx `goesLast`). */
  youth: boolean | null;
}

/**
 * The picker's leagues — every competition with matches, men's AND women's (never
 * gender-filtered), in Catalyst's own order (country, then name).
 *
 * Widgets store a league's id (ids-2); the label is only what the picker shows, so it
 * matches the snapshot site and tells leagues apart:
 * - A league the snapshot knows keeps the snapshot's name ("FAWSL", "NWSL" — matched by
 *   the snapshot's own league id), and goes FIRST: a fresh table opens on FAWSL, as on
 *   the snapshot site.
 * - Otherwise the DB name, with the country added only where two leagues share a name
 *   (e.g. five "Premier League"s), and the id only where even that clashes.
 */
export function leagueChoices(comps: CompetitionSummary[], snapshotNames: ReadonlyMap<CompetitionId, string>): LiveLeagueChoice[] {
  const nameCount = new Map<string, number>();
  for (const c of comps) nameCount.set(c.name, (nameCount.get(c.name) ?? 0) + 1);
  const known: LiveLeagueChoice[] = [];
  const rest: LiveLeagueChoice[] = [];
  for (const c of comps) {
    const snapshotName = snapshotNames.get(c.id);
    if (snapshotName) {
      known.push({
        label: snapshotName,
        id: c.id,
        womens: c.competitionWomens,
        countryName: c.countryName,
        countryCode: c.countryCode,
        youth: c.competitionYouth,
      });
      continue;
    }
    const shared = (nameCount.get(c.name) ?? 0) > 1;
    rest.push({
      label: shared ? `${c.name} (${c.countryName ?? "International"})` : c.name,
      id: c.id,
      womens: c.competitionWomens,
      countryName: c.countryName,
      countryCode: c.countryCode,
      youth: c.competitionYouth,
    });
  }
  // Name AND country can still clash (the DB has two country-less "Super League"s, ids 1975
  // + 2026): only those get their id as a last tie-break.
  const labelCount = new Map<string, number>();
  for (const l of rest) labelCount.set(l.label, (labelCount.get(l.label) ?? 0) + 1);
  for (const l of rest) if ((labelCount.get(l.label) ?? 0) > 1) l.label = `${l.label} #${l.id}`;
  // Snapshot order for the known ones (the order its picker always had).
  const order = [...snapshotNames.values()];
  known.sort((a, b) => order.indexOf(a.label) - order.indexOf(b.label));
  const choices = [...known, ...rest];
  const labels = new Set(choices.map((c) => c.label));
  // Fail fast: two leagues with one label would look the same in the picker — a person
  // couldn't tell which table they're opening.
  if (labels.size !== choices.length) throw new Error("League labels aren't unique — leagueChoices needs a better tie-break.");
  return choices;
}

export function toProtoLeagueTable(
  league: LeagueChoice,
  res: LeagueTableResponse,
  seasonCode: string,
  seasonCodes: string[],
): ProtoLeagueTable {
  return {
    competition: league.label,
    leagueCode: String(league.id),
    seasonCode,
    seasonCodes,
    matchCount: res.matchCount,
    table: res.table,
  };
}

/** `/league-table`'s 404 = "no completed matches for that league + season" — a real, known
 *  answer (a season whose fixtures are loaded but not played yet), not a failure. */
async function tableOrNone(api: LiveApi, id: CompetitionId, seasonCode: string): Promise<LeagueTableResponse | null> {
  try {
    return await api.get<LeagueTableResponse>(`/league-table?competitionId=${id}&seasonId=${seasonCode}`);
  } catch (err) {
    if (err instanceof LiveApiError && err.status === 404) return null;
    throw err;
  }
}

/**
 * A league's table for `season`, or — with no season asked — for the newest season that
 * has a played match (decided 2026-10-05). `/seasons` already lists THIS league's seasons
 * newest first, by its own latest match date, so MLS (2026) and La Liga (2026/27) each get
 * their own newest with no calendar rules. A brand-new season whose fixtures are loaded
 * but unplayed has no table yet, so the one before it opens instead (the new one stays
 * pickable).
 */
export async function loadLeagueTable(api: LiveApi, league: LeagueChoice, season?: string): Promise<ProtoLeagueTable> {
  const { seasons } = await api.get<SeasonsResponse>(`/seasons?competitionId=${league.id}`);
  const seasonCodes = seasons.map((s) => String(s.id));
  const empty = (seasonCode: string): ProtoLeagueTable => ({
    competition: league.label,
    leagueCode: String(league.id),
    seasonCode,
    seasonCodes,
    matchCount: 0,
    table: [],
  });

  if (season && seasonCodes.includes(season)) {
    const res = await tableOrNone(api, league.id, season);
    return res ? toProtoLeagueTable(league, res, season, seasonCodes) : empty(season);
  }
  for (const code of seasonCodes) {
    const res = await tableOrNone(api, league.id, code);
    if (res) return toProtoLeagueTable(league, res, code, seasonCodes);
  }
  // No season of this league has a played match (e.g. a cup with only fixtures): the
  // widget's own "No table data …" message.
  return empty(seasonCodes[0] ?? "");
}

export async function loadLeagueChoices(api: LiveApi, snapshotNames: ReadonlyMap<CompetitionId, string>): Promise<LiveLeagueChoice[]> {
  const { competitions } = await api.get<CompetitionsResponse>("/competitions");
  return leagueChoices(competitions, snapshotNames);
}
